import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Clock, AlertTriangle, Database, TrendingUp, Users, Globe, Zap, Shield } from 'lucide-react';
import CLEARArchitectureDiagram from './graphics/CLEARArchitectureDiagram';

interface AboutPageProps {
  onHome?: () => void;
}

const AboutPage: React.FC<AboutPageProps> = ({ onHome }) => {
  const { t } = useTranslation('about');

  const problemAreas = [
    {
      icon: Clock,
      key: 'dataVelocity',
      stat: '72-96 hours',
      color: '#FF4C00'
    },
    {
      icon: Database,
      key: 'fragmentation',
      stat: '4-6 hours daily',
      color: '#FF6B35'
    },
    {
      icon: TrendingUp,
      key: 'predictive',
      stat: '10+ years unused',
      color: '#8B8680'
    },
    {
      icon: AlertTriangle,
      key: 'scale',
      stat: 'System failures',
      color: '#FF4C00'
    }
  ];

  const impactStats = [
    { number: '300M', label: 'People requiring humanitarian assistance globally', color: '#FF4C00' },
    { number: '$32B', label: 'Funding gap in humanitarian response', color: '#FF6B35' },
    { number: '30%', label: 'Damage reduction possible with early warning', color: '#8B8680' },
    { number: '8x', label: 'Higher mortality in areas without early warning', color: '#FF4C00' }
  ];

  const visionItems = [
    {
      icon: AlertTriangle,
      key: 'hoursMatter',
      color: '#FF4C00'
    },
    {
      icon: Users,
      key: 'anticipation',
      color: '#FF6B35'
    },
    {
      icon: Database,
      key: 'unifiedIntel',
      color: '#8B8680'
    },
    {
      icon: Globe,
      key: 'globalScale',
      color: '#FF4C00'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content */}
      <div className="pt-20">
      {/* Hero Section */}
      <section className="py-32 bg-white">
        <div className="spectrum-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="spectrum-headline text-4xl lg:text-6xl spectrum-text-dark mb-8 leading-tight">
              {t('hero.title')}
            </h1>
            <p className="spectrum-body text-xl spectrum-text-muted max-w-4xl mx-auto leading-relaxed mb-12">
              {t('hero.subtitle')}
            </p>
            
            <div className="inline-flex items-center gap-4 bg-white border-2 border-gray-300 px-8 py-4">
              <Users className="w-6 h-6" style={{color: '#FF4C00'}} />
              <span className="spectrum-body text-lg spectrum-text-dark">
                {t('hero.nrcExperience')}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem Statement Section */}
      <section className="py-32 spectrum-bg-light">
        <div className="spectrum-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="spectrum-headline text-3xl lg:text-5xl spectrum-text-dark mb-8 leading-tight">
              {t('problem.title')}
            </h2>
            <p className="spectrum-body text-xl spectrum-text-muted max-w-4xl mx-auto leading-relaxed">
              {t('problem.subtitle')}
            </p>
          </motion.div>

          {/* Problem Areas Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {problemAreas.map((problem, index) => (
              <motion.div
                key={problem.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="spectrum-card text-center"
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-white border border-gray-300 flex items-center justify-center">
                    <problem.icon className="w-8 h-8" style={{color: problem.color}} />
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="spectrum-headline text-2xl mb-2" style={{color: problem.color}}>
                    {problem.stat}
                  </div>
                  <h3 className="spectrum-headline text-lg spectrum-text-dark font-bold mb-4">
                    {t(`problem.areas.${problem.key}.title`)}
                  </h3>
                </div>
                
                <p className="spectrum-body text-sm spectrum-text-muted leading-relaxed">
                  {t(`problem.areas.${problem.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Global Impact Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-white border-2 border-gray-300 p-12 lg:p-16"
          >
            <div className="text-center mb-12">
              <h3 className="spectrum-headline text-2xl lg:text-3xl spectrum-text-dark mb-6">
                {t('problem.impact.title')}
              </h3>
              <p className="spectrum-body text-lg spectrum-text-muted max-w-3xl mx-auto">
                {t('problem.impact.subtitle')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {impactStats.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-6 bg-gray-50 border border-gray-300"
                >
                  <div className="spectrum-headline text-4xl mb-3" style={{color: stat.color}}>
                    {stat.number}
                  </div>
                  <div className="spectrum-body text-sm spectrum-text-muted leading-tight">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Data Paradox Section */}
      <section className="py-32 bg-white">
        <div className="spectrum-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="spectrum-headline text-3xl lg:text-5xl spectrum-text-dark mb-8 leading-tight">
              {t('dataParadox.title')}
            </h2>
            <p className="spectrum-body text-xl spectrum-text-muted max-w-4xl mx-auto leading-relaxed mb-12">
              {t('dataParadox.subtitle')}
            </p>
          </motion.div>

          {/* Critical Junctures */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
            {[
              { key: 'velocity', icon: Clock, color: '#FF4C00' },
              { key: 'fragmentation', icon: Database, color: '#FF6B35' },
              { key: 'predictive', icon: TrendingUp, color: '#8B8680' },
              { key: 'scale', icon: AlertTriangle, color: '#FF4C00' }
            ].map((item, index) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="spectrum-card"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white border border-gray-300 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6" style={{color: item.color}} />
                  </div>
                  <div className="flex-1">
                    <h3 className="spectrum-headline text-lg spectrum-text-dark font-bold mb-3">
                      {t(`dataParadox.junctures.${item.key}.title`)}
                    </h3>
                    <p className="spectrum-body text-sm spectrum-text-muted leading-relaxed">
                      {t(`dataParadox.junctures.${item.key}.description`)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Stakeholders and Opportunities */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Stakeholders */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="spectrum-card"
            >
              <h3 className="spectrum-headline text-xl spectrum-text-dark font-bold mb-6">
                {t('dataParadox.stakeholders.title')}
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="spectrum-body text-sm font-semibold spectrum-text-dark">
                      {t('dataParadox.stakeholders.primary.title')}
                    </p>
                    <p className="spectrum-body text-xs spectrum-text-muted">
                      {t('dataParadox.stakeholders.primary.description')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="spectrum-body text-sm font-semibold spectrum-text-dark">
                      {t('dataParadox.stakeholders.operational.title')}
                    </p>
                    <p className="spectrum-body text-xs spectrum-text-muted">
                      {t('dataParadox.stakeholders.operational.description')}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="spectrum-body text-sm font-semibold spectrum-text-dark">
                      {t('dataParadox.stakeholders.strategic.title')}
                    </p>
                    <p className="spectrum-body text-xs spectrum-text-muted">
                      {t('dataParadox.stakeholders.strategic.description')}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* New Opportunities */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="spectrum-card"
            >
              <h3 className="spectrum-headline text-xl spectrum-text-dark font-bold mb-6">
                {t('dataParadox.opportunities.title')}
              </h3>
              <p className="spectrum-body text-sm spectrum-text-muted leading-relaxed">
                {t('dataParadox.opportunities.description')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cascading Crises Section */}
      <section className="py-32 spectrum-bg-light">
        <div className="spectrum-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="spectrum-headline text-3xl lg:text-5xl spectrum-text-dark mb-8 leading-tight">
              {t('cascadingCrises.title')}
            </h2>
            <p className="spectrum-body text-xl spectrum-text-muted max-w-4xl mx-auto leading-relaxed">
              {t('cascadingCrises.subtitle')}
            </p>
          </motion.div>

          {/* Crisis Patterns */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            {[
              { key: 'climate', color: '#FF4C00', icon: TrendingUp },
              { key: 'conflict', color: '#FF6B35', icon: AlertTriangle },
              { key: 'displacement', color: '#8B8680', icon: Users }
            ].map((pattern, index) => (
              <motion.div
                key={pattern.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="spectrum-card text-center"
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-white border border-gray-300 flex items-center justify-center">
                    <pattern.icon className="w-8 h-8" style={{color: pattern.color}} />
                  </div>
                </div>
                <h3 className="spectrum-headline text-lg spectrum-text-dark font-bold mb-4">
                  {t(`cascadingCrises.patterns.${pattern.key}.title`)}
                </h3>
                <p className="spectrum-body text-sm spectrum-text-muted leading-relaxed">
                  {t(`cascadingCrises.patterns.${pattern.key}.description`)}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Compound Risk Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white border-2 border-gray-300 p-12 lg:p-16"
          >
            <div className="text-center mb-12">
              <h3 className="spectrum-headline text-2xl lg:text-3xl spectrum-text-dark mb-6">
                {t('cascadingCrises.compoundRisk.title')}
              </h3>
              <p className="spectrum-body text-lg spectrum-text-muted max-w-3xl mx-auto">
                {t('cascadingCrises.compoundRisk.subtitle')}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { number: '75.9M', label: 'Internally displaced people by end of 2023', color: '#FF4C00' },
                { number: '90M', label: 'Forcibly displaced living in climate-hazard areas', color: '#FF6B35' },
                { number: '200M', label: 'Will need assistance by 2050 due to climate', color: '#8B8680' }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center p-6 bg-gray-50 border border-gray-300"
                >
                  <div className="spectrum-headline text-3xl mb-3" style={{color: stat.color}}>
                    {stat.number}
                  </div>
                  <div className="spectrum-body text-sm spectrum-text-muted leading-tight">
                    {stat.label}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-32 bg-white">
        <div className="spectrum-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <h2 className="spectrum-headline text-3xl lg:text-5xl spectrum-text-dark mb-8 leading-tight">
              {t('vision.title')}
            </h2>
            <p className="spectrum-body text-xl spectrum-text-muted max-w-4xl mx-auto leading-relaxed">
              {t('vision.subtitle')}
            </p>
          </motion.div>

          {/* Vision Items */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
            {visionItems.map((item, index) => (
              <motion.div
                key={item.key}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="spectrum-card"
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-white border-2 border-gray-400 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-8 h-8" style={{color: item.color}} />
                  </div>
                  <div className="flex-1">
                    <h3 className="spectrum-headline text-xl spectrum-text-dark font-bold mb-4">
                      {t(`vision.items.${item.key}.title`)}
                    </h3>
                    <p className="spectrum-body text-lg spectrum-text-muted leading-relaxed">
                      {t(`vision.items.${item.key}.description`)}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Mission Statement */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center bg-white border-2 p-12 lg:p-20"
            style={{borderColor: '#FF4C00'}}
          >
            <Shield className="w-20 h-20 mx-auto mb-8" style={{color: '#FF4C00'}} />
            <h3 className="spectrum-headline text-2xl lg:text-3xl spectrum-text-dark mb-8">
              {t('mission.title')}
            </h3>
            <p className="spectrum-body text-xl spectrum-text-dark max-w-4xl mx-auto leading-relaxed">
              {t('mission.statement')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* CLEAR Architecture Section */}
      <section className="py-32 spectrum-bg-light">
        <div className="spectrum-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="spectrum-headline text-3xl lg:text-5xl spectrum-text-dark mb-8 leading-tight">
              System Architecture
            </h2>
            <p className="spectrum-body text-xl spectrum-text-muted max-w-4xl mx-auto leading-relaxed mb-12">
              CLEAR transforms humanitarian operations through four integrated AI-powered components built on a unified data foundation that compresses response timelines and enables anticipatory action
            </p>
          </motion.div>

          {/* Interactive Architecture Diagram */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-white border-2 border-gray-300 p-8 lg:p-12 mb-16"
          >
            <CLEARArchitectureDiagram 
              className="w-full h-[600px]" 
              animated={true}
            />
          </motion.div>

          {/* Component Integration Explanation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-12"
          >
            {/* How Components Work Together */}
            <div className="spectrum-card">
              <h3 className="spectrum-headline text-xl spectrum-text-dark font-bold mb-6">
                Component Integration
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-orange-500 flex items-center justify-center text-white text-xs font-bold">1</div>
                  <div>
                    <p className="spectrum-body text-sm font-semibold spectrum-text-dark">
                      Detection triggers response
                    </p>
                    <p className="spectrum-body text-xs spectrum-text-muted">
                      Alert Engine identifies crisis → notifies Response Console → teams mobilize
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-orange-500 flex items-center justify-center text-white text-xs font-bold">2</div>
                  <div>
                    <p className="spectrum-body text-sm font-semibold spectrum-text-dark">
                      Analysis guides action
                    </p>
                    <p className="spectrum-body text-xs spectrum-text-muted">
                      Situation Analysis informs where/how to respond → Response Console coordinates implementation
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-orange-500 flex items-center justify-center text-white text-xs font-bold">3</div>
                  <div>
                    <p className="spectrum-body text-sm font-semibold spectrum-text-dark">
                      Cash follows assessment
                    </p>
                    <p className="spectrum-body text-xs spectrum-text-muted">
                      Field teams verify needs via Console → triggers Distribution Pipeline → assistance delivered
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-orange-500 flex items-center justify-center text-white text-xs font-bold">4</div>
                  <div>
                    <p className="spectrum-body text-sm font-semibold spectrum-text-dark">
                      Continuous feedback
                    </p>
                    <p className="spectrum-body text-xs spectrum-text-muted">
                      All components feed data back to improve detection models and response protocols
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Performance Metrics */}
            <div className="spectrum-card">
              <h3 className="spectrum-headline text-xl spectrum-text-dark font-bold mb-6">
                Performance Impact
              </h3>
              <div className="space-y-6">
                <div className="text-center p-4 bg-gray-50 border border-gray-300">
                  <div className="spectrum-headline text-2xl mb-2" style={{color: '#FF4C00'}}>
                    6 weeks
                  </div>
                  <div className="spectrum-body text-sm spectrum-text-muted">
                    Early crisis detection (Somalia drought example)
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-50 border border-gray-300">
                  <div className="spectrum-headline text-2xl mb-2" style={{color: '#FF6B35'}}>
                    85%
                  </div>
                  <div className="spectrum-body text-sm spectrum-text-muted">
                    Detection accuracy with human feedback loop
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-50 border border-gray-300">
                  <div className="spectrum-headline text-2xl mb-2" style={{color: '#8B8680'}}>
                    15 mins
                  </div>
                  <div className="spectrum-body text-sm spectrum-text-muted">
                    Report generation (previously 6 hours)
                  </div>
                </div>
                <div className="text-center p-4 bg-gray-50 border border-gray-300">
                  <div className="spectrum-headline text-2xl mb-2" style={{color: '#FF4C00'}}>
                    30+
                  </div>
                  <div className="spectrum-body text-sm spectrum-text-muted">
                    Languages processed without translation delays
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-24 spectrum-bg-light">
        <div className="spectrum-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="spectrum-headline text-3xl lg:text-4xl spectrum-text-dark mb-8">
              {t('cta.title')}
            </h2>
            <p className="spectrum-body text-lg spectrum-text-muted mb-12 max-w-3xl mx-auto">
              {t('cta.subtitle')}
            </p>
            
            <motion.button
              onClick={() => {
                const signupSection = document.querySelector('[data-signup-section]');
                signupSection?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="spectrum-button text-lg px-12 py-4"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {t('cta.button')}
            </motion.button>
          </motion.div>
        </div>
      </section>
      </div>
    </div>
  );
};

export default AboutPage;